import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
/**
 * Analytics Page
 * Per-dish consumption analytics with interactive charts and configurable time periods
 */

import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {
  ChartGrid,
  GoalTrendChart,
  PerDishConsumptionChart,
  UtilityCostPerDishSection,
} from '../components/ui';
import { EmissionsByScopeChart } from '../components/ui/EmissionsByScopeChart';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { featureFlags } from '../config/featureFlags';
import {
  useAnalyticsCharts,
  useCalendarizedConsumption,
  useCoversVolumes,
  useCalendarizedCO2e,
  useCalendarizedEmissionsByScope,
  useGoals,
} from '../hooks/useApi';
import type { ChartFilters } from '../types/api';
import {
  calculatePerDishMetrics,
  generateDateRangeFromFilters,
} from '../utils/perDishCalculations';
import { useAuthProfile } from '../stores/authStore';
import {
  GenerateReportButton,
  DownloadReportBanner,
} from '../features/reports';
import type { MarketingReportTimeRange } from '../features/reports';
import { getReportCopy } from '../features/reports/lib/reportCopy';
import type { PaginatedResponse, CoversVolume } from '../types/api';
import {
  buildGoalChartPlan,
  getGoalDisplayRange,
} from '../features/goals/goalChartProjection';
import {
  clipPerDishMetricsToCompletedMonths,
  getPerDishActuals,
  padPerDishMetricsToRange,
} from '../features/goals/analyticsGoalMilestones';
import {
  findLatestJobForRestaurant,
  selectRemoveReportJob,
  selectReportJobs,
  useReportJobStore,
} from '../features/reports/store/reportJob.store';
// Chart data transformation utilities are no longer needed - using direct API data
// Date formatting is now handled inline to avoid timezone issues

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format date object to YYYY-MM-DD string
 */
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate date ranges for quick selection
 */
const getDateRange = (
  period: 'monthly' | 'quarterly' | '6-months' | 'yearly',
) => {
  const endDate = new Date();
  const startDate = new Date();

  switch (period) {
    case 'monthly':
      startDate.setMonth(startDate.getMonth() - 1);
      break;
    case 'quarterly':
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    case '6-months':
      startDate.setMonth(startDate.getMonth() - 6);
      break;
    case 'yearly':
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
  }

  return {
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
  };
};

// ============================================================================
// Styled Components
// ============================================================================

const AnalyticsContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const AnalyticsContent = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled(SharedPageTitle)``;

const PageSubtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const ControlsSection = styled.div`
  margin-bottom: 2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;

  /* Mobile devices - stack controls */
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  /* Mobile devices - stack filter components */
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
  }
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;

  /* Mobile devices - full width */
  @media (max-width: 767px) {
    min-width: auto;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  > span {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--text-muted);
  }
`;

const SelectElement = styled.select`
  display: block;
  appearance: none;
  padding: 0.625rem 2.5rem 0.625rem 0.875rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--surface);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  cursor: pointer;
  min-width: 180px;
  height: 44px;

  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }

  /* Mobile devices - full width */
  @media (max-width: 767px) {
    width: 100%;
    min-width: auto;
    height: 44px;
  }
`;

const RefreshButton = styled(Button)`
  min-height: 44px;
`;

const ReportButtonWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;

  @media (max-width: 767px) {
    width: 100%;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ReportBannerContainer = styled.div`
  margin-bottom: 2rem;
`;

const ChartSection = styled.section`
  margin-bottom: 3rem;
`;

const SectionHeader = styled.div`
  max-width: 720px;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.25rem;
  color: var(--text-primary);
  font-size: 1.125rem;
  line-height: var(--line-height-tight);
`;

const SectionDescription = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: var(--line-height-relaxed);
`;

const ErrorBanner = styled.div`
  background: var(--danger-50);
  border: 1px solid var(--danger-100);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 2rem;
  color: var(--danger-600);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const WarningBanner = styled.div`
  background: var(--warning-50);
  border: 1px solid var(--warning-100);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 2rem;
  color: var(--warning-600);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BannerContent = styled.div`
  flex: 1;
`;

const BannerTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const BannerMessage = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * Analytics Page Component
 * Displays per-dish consumption metrics in interactive charts
 */
export const AnalyticsPage: React.FC = () => {
  const [filters, setFilters] = useState<ChartFilters>({
    period_type: 'monthly',
    ...getDateRange('quarterly'), // Default to 3-month rolling period
  });
  const [dateRange, setDateRange] = useState<
    'monthly' | 'quarterly' | '6-months' | 'yearly'
  >('quarterly');
  const profile = useAuthProfile();
  const restaurantId = profile?.id ?? null;
  const marketingReportsEnabled = featureFlags.MARKETING_REPORTS;
  const reportJobs = useReportJobStore(selectReportJobs);
  const removeReportJob = useReportJobStore(selectRemoveReportJob);
  const persistedReportJob = useMemo(
    () => findLatestJobForRestaurant(reportJobs, restaurantId),
    [reportJobs, restaurantId],
  );

  // Fetch chart data
  const {
    data: chartData,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useAnalyticsCharts(filters);

  // Generate date range for calendarized consumption API
  const dateRangeForApi = generateDateRangeFromFilters(filters);

  // Fetch calendarized consumption data
  const {
    data: calendarizedData,
    isLoading: isLoadingCalendarized,
    error: calendarizedError,
    refetch: refetchCalendarized,
    isRefetching: isRefetchingCalendarized,
  } = useCalendarizedConsumption(
    dateRangeForApi || { start_month: '2024-01', end_month: '2024-12' },
  );

  // Fetch calendarized CO2e data
  const {
    data: calendarizedCO2eData,
    isLoading: isLoadingCalendarizedCO2e,
    error: calendarizedCO2eError,
    refetch: refetchCalendarizedCO2e,
  } = useCalendarizedCO2e(
    dateRangeForApi || { start_month: '2024-01', end_month: '2024-12' },
  );

  // Fetch calendarized emissions by scope data
  const {
    data: emissionsByScopeData,
    isLoading: isLoadingEmissionsByScope,
    error: emissionsByScopeError,
    refetch: refetchEmissionsByScope,
  } = useCalendarizedEmissionsByScope(
    dateRangeForApi || { start_month: '2024-01', end_month: '2024-12' },
  );

  // Fetch covers volumes (sales data)
  const {
    data: coversVolumes,
    isLoading: isLoadingCovers,
    error: coversError,
    refetch: refetchCovers,
    isRefetching: isRefetchingCovers,
  } = useCoversVolumes();

  const { data: goals = [], isLoading: isLoadingGoals } = useGoals();
  const selectedGoalRange = useMemo(
    () =>
      dateRangeForApi
        ? {
            startMonth: dateRangeForApi.start_month,
            endMonth: dateRangeForApi.end_month,
          }
        : null,
    [dateRangeForApi],
  );
  const displayGoalRange = useMemo(
    () =>
      selectedGoalRange ? getGoalDisplayRange(selectedGoalRange, goals) : null,
    [goals, selectedGoalRange],
  );

  const handleDateRangeChange = (
    range: 'monthly' | 'quarterly' | '6-months' | 'yearly',
  ) => {
    setDateRange(range);
    setFilters((prev) => ({
      ...prev,
      ...getDateRange(range),
    }));
  };

  const reportTimeRange = useMemo<MarketingReportTimeRange>(() => {
    if (filters.start_date && filters.end_date) {
      return {
        type: 'CUSTOM',
        start: filters.start_date,
        end: filters.end_date,
      };
    }
    return {
      type: 'LAST_FULL_MONTH',
    };
  }, [filters.start_date, filters.end_date]);

  const reportCopy = useMemo(() => getReportCopy('es-ES'), []);
  const reportButtonRef = React.useRef<HTMLButtonElement>(null);
  const reportWarnings = useMemo(() => {
    const warnings: string[] = [];

    if (!chartData || !calendarizedData) {
      warnings.push(reportCopy.preflight.dataStillLoading);
    }
    if (chartData && !chartData.has_covers_volume_data) {
      warnings.push(reportCopy.preflight.missingCovers);
    }
    if (chartData?.missing_periods.length) {
      warnings.push(
        reportCopy.preflight.missingPeriods.replace(
          '{{periods}}',
          chartData.missing_periods.join(', '),
        ),
      );
    }
    if (calendarizedData?.data.length === 0) {
      warnings.push(reportCopy.preflight.missingConsumption);
    }

    return warnings;
  }, [calendarizedData, chartData, reportCopy]);

  const handleReportGenerated = useCallback(
    (_: {
      jobId: string;
      downloadUrl: string;
      expiresAt: string | null;
      reportType: string | null;
      startedAt: string | null;
    }) => {
      toast.success(reportCopy.toast.success);
    },
    [reportCopy],
  );

  const handleReportFailed = useCallback(
    (error: Error) => {
      toast.error(error.message || reportCopy.toast.failure);
    },
    [reportCopy],
  );

  const handleReportDismiss = useCallback(() => {
    if (persistedReportJob?.jobId) {
      removeReportJob(persistedReportJob.jobId);
    }
  }, [persistedReportJob?.jobId, removeReportJob]);

  const handleReportRetry = useCallback(() => {
    if (persistedReportJob?.jobId) {
      removeReportJob(persistedReportJob.jobId);
    }
    reportButtonRef.current?.click();
  }, [persistedReportJob?.jobId, removeReportJob]);

  const bannerStatus = persistedReportJob?.status ?? 'idle';
  const bannerDownloadUrl = persistedReportJob?.downloadUrl ?? null;
  const bannerExpiresAt = persistedReportJob?.expiresAt ?? null;
  const bannerJobId = persistedReportJob?.jobId ?? null;

  const shouldShowReportBanner =
    marketingReportsEnabled &&
    persistedReportJob !== null &&
    persistedReportJob.status !== 'idle';

  const handleRefresh = () => {
    refetch();
    refetchCalendarized();
    refetchCovers();
    refetchCalendarizedCO2e();
    refetchEmissionsByScope();
    toast.success('Datos actualizados');
  };

  // Calculate per-dish metrics from calendarized consumption and covers data
  const perDishMetrics = useMemo(() => {
    if (!calendarizedData?.data) {
      return [];
    }

    const coversSource =
      coversVolumes ??
      ({
        items: [],
        total: 0,
        page: 1,
        per_page: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      } satisfies PaginatedResponse<CoversVolume>);

    return calculatePerDishMetrics(
      calendarizedData.data,
      calendarizedCO2eData?.data,
      coversSource,
    );
  }, [calendarizedData, calendarizedCO2eData, coversVolumes]);

  const completedPerDishMetrics = useMemo(
    () => clipPerDishMetricsToCompletedMonths(perDishMetrics),
    [perDishMetrics],
  );
  const displayedPerDishMetrics = useMemo(
    () =>
      displayGoalRange
        ? padPerDishMetricsToRange(completedPerDishMetrics, displayGoalRange)
        : completedPerDishMetrics,
    [completedPerDishMetrics, displayGoalRange],
  );

  const perDishDataInitialLoading = isLoadingCalendarized || isLoadingCovers;

  const wasteDiversionData = useMemo(
    () =>
      completedPerDishMetrics.flatMap((month) => {
        const recyclables = month.kg_recyclables_total ?? 0;
        const organic = month.kg_organic_waste_total ?? 0;
        const general = month.kg_general_waste_total ?? 0;
        const total = recyclables + organic + general;
        if (total <= 0) return [];

        return [
          {
            date: `${month.year}-${String(month.month).padStart(2, '0')}`,
            value: ((recyclables + organic) / total) * 100,
          },
        ];
      }),
    [completedPerDishMetrics],
  );

  const goalPlans = useMemo(() => {
    if (!displayGoalRange) return null;
    const buildPlan = (
      chartKey: 'kwh' | 'm3_water' | 'm3_gas' | 'kg_general_waste' | 'kg_co2e',
      valueKey:
        | 'kwh_per_dish'
        | 'm3_water_per_dish'
        | 'm3_gas_per_dish'
        | 'kg_general_waste_per_dish'
        | 'kg_co2e_per_dish',
    ) =>
      buildGoalChartPlan({
        goals,
        chartKey,
        actuals: getPerDishActuals(completedPerDishMetrics, valueKey),
        visibleRange: displayGoalRange,
      });

    return {
      kwh: buildPlan('kwh', 'kwh_per_dish'),
      m3_water: buildPlan('m3_water', 'm3_water_per_dish'),
      m3_gas: buildPlan('m3_gas', 'm3_gas_per_dish'),
      kg_general_waste: buildPlan(
        'kg_general_waste',
        'kg_general_waste_per_dish',
      ),
      kg_co2e: buildPlan('kg_co2e', 'kg_co2e_per_dish'),
      waste_diversion_pct: buildGoalChartPlan({
        goals,
        chartKey: 'waste_diversion_pct',
        actuals: wasteDiversionData,
        visibleRange: displayGoalRange,
      }),
    };
  }, [completedPerDishMetrics, displayGoalRange, goals, wasteDiversionData]);

  const perDishDataIsRefetching =
    !perDishDataInitialLoading &&
    calendarizedData !== undefined &&
    (isRefetchingCalendarized || isRefetchingCovers);

  const resourceChartConfigs = [
    {
      key: 'kwh',
      consumptionType: 'kwh' as const,
      title: 'Energía Eléctrica',
      goalChartKey: 'kwh' as const,
    },
    {
      key: 'm3_water',
      consumptionType: 'm3_water' as const,
      title: 'Consumo de Agua',
      goalChartKey: 'm3_water' as const,
    },
    {
      key: 'm3_gas',
      consumptionType: 'm3_gas' as const,
      title: 'Consumo de Gas',
      goalChartKey: 'm3_gas' as const,
    },
  ];

  const wasteChartConfigs = [
    {
      key: 'kg_recyclables',
      consumptionType: 'kg_recyclables' as const,
      title: 'Residuos Reciclables',
    },
    {
      key: 'kg_organic_waste',
      consumptionType: 'kg_organic_waste' as const,
      title: 'Residuos Orgánicos',
    },
    {
      key: 'kg_general_waste',
      consumptionType: 'kg_general_waste' as const,
      title: 'Residuos Generales',
      goalChartKey: 'kg_general_waste' as const,
    },
  ];

  // CO2e chart configuration (uses a different component)
  const co2eChartConfig = {
    key: 'kg_co2e',
    consumptionType: 'kg_co2e' as const,
    title: 'Emisiones de CO₂e',
  };

  const consumptionErrorMessage =
    calendarizedError?.message || coversError?.message || null;

  // Old chart configuration removed - using new per-dish chart system

  // Check if any of the main data is loading
  const isAnyLoading =
    isLoading ||
    isLoadingCalendarized ||
    isLoadingCovers ||
    isLoadingCalendarizedCO2e ||
    isLoadingEmissionsByScope ||
    isLoadingGoals;

  if (isAnyLoading) {
    return (
      <AnalyticsContainer>
        <AnalyticsContent>
          <LoadingSpinner message="Cargando análisis de consumo..." />
        </AnalyticsContent>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <AnalyticsContent>
        <PageHeader>
          <PageTitle>Análisis</PageTitle>
          <PageSubtitle>
            Métricas de consumo por plato servido con análisis temporal
          </PageSubtitle>
        </PageHeader>

        <ControlsSection>
          <FilterGroup>
            <FilterLabel htmlFor="analytics-date-range">Rango</FilterLabel>
            <SelectWrapper>
              <SelectElement
                id="analytics-date-range"
                value={dateRange}
                onChange={(e) =>
                  handleDateRangeChange(
                    e.target.value as
                      | 'monthly'
                      | 'quarterly'
                      | '6-months'
                      | 'yearly',
                  )
                }
              >
                <option value="monthly">Último mes</option>
                <option value="quarterly">Últimos 3 meses</option>
                <option value="6-months">Últimos 6 meses</option>
                <option value="yearly">Último año</option>
              </SelectElement>
              <Icon name="chevron-down" size="sm" aria-hidden />
            </SelectWrapper>
          </FilterGroup>

          <RefreshButton
            onClick={handleRefresh}
            loading={isRefetching}
            startIcon={<Icon name="refresh" size="sm" />}
          >
            {isRefetching ? 'Actualizando...' : 'Actualizar'}
          </RefreshButton>

          {marketingReportsEnabled && (
            <ReportButtonWrapper>
              <GenerateReportButton
                ref={reportButtonRef}
                restaurantId={restaurantId}
                timeRange={reportTimeRange}
                locale="es-ES"
                includeBenchmarks={false}
                preflightWarnings={reportWarnings}
                onGenerated={handleReportGenerated}
                onFailed={handleReportFailed}
              >
                {reportCopy.button.generate}
              </GenerateReportButton>
            </ReportButtonWrapper>
          )}
        </ControlsSection>

        {shouldShowReportBanner && (
          <ReportBannerContainer>
            <DownloadReportBanner
              status={bannerStatus}
              downloadUrl={bannerDownloadUrl}
              expiresAt={bannerExpiresAt}
              jobId={bannerJobId}
              restaurantId={restaurantId}
              onDismiss={handleReportDismiss}
              onRetry={handleReportRetry}
              locale="es-ES"
            />
          </ReportBannerContainer>
        )}

        {error && (
          <ErrorBanner>
            <Icon name="error" size="md" />
            <BannerContent>
              <BannerTitle>Error al cargar datos</BannerTitle>
              <BannerMessage>
                No se pudieron cargar los datos de análisis. Por favor, intenta
                de nuevo.
              </BannerMessage>
            </BannerContent>
          </ErrorBanner>
        )}

        {chartData && chartData.missing_periods.length > 0 && (
          <WarningBanner>
            <Icon name="warning" size="md" />
            <BannerContent>
              <BannerTitle>Faltan datos de servicio</BannerTitle>
              <BannerMessage>
                Para calcular métricas por plato, necesitas registrar el número
                de cubiertos servidos en la sección "Datos de Ventas".
                {chartData.missing_periods.length > 0 && (
                  <span>
                    {' '}
                    Períodos sin datos: {chartData.missing_periods.join(', ')}
                  </span>
                )}
              </BannerMessage>
            </BannerContent>
          </WarningBanner>
        )}

        <UtilityCostPerDishSection
          data={perDishMetrics}
          currency={calendarizedData?.currency ?? profile?.currency ?? 'EUR'}
          loading={perDishDataInitialLoading}
          missingCostRecords={calendarizedData?.missing_cost_records ?? 0}
          excludedCurrencyRecords={
            calendarizedData?.excluded_currency_records ?? 0
          }
          {...(calendarizedError || coversError
            ? {
                error:
                  calendarizedError?.message ||
                  coversError?.message ||
                  'No se pudieron cargar los costos por plato.',
              }
            : {})}
        />

        <ChartSection>
          <SectionHeader>
            <SectionTitle>Eficiencia de recursos</SectionTitle>
            <SectionDescription>
              Electricidad, agua y gas por plato servido durante el período.
            </SectionDescription>
          </SectionHeader>
          <ChartGrid>
            {resourceChartConfigs.map((chartConfig) => (
              <PerDishConsumptionChart
                key={chartConfig.key}
                data={displayedPerDishMetrics}
                consumptionType={chartConfig.consumptionType}
                title={chartConfig.title}
                loading={perDishDataInitialLoading}
                isRefetching={perDishDataIsRefetching}
                error={consumptionErrorMessage || undefined}
                goalPlan={goalPlans?.[chartConfig.goalChartKey] ?? null}
              />
            ))}
          </ChartGrid>
        </ChartSection>

        <ChartSection>
          <SectionHeader>
            <SectionTitle>Gestión de residuos</SectionTitle>
            <SectionDescription>
              Evolución de residuos reciclables, orgánicos y generales, junto
              con el porcentaje desviado del vertedero.
            </SectionDescription>
          </SectionHeader>
          <ChartGrid>
            {wasteChartConfigs.map((chartConfig) => (
              <PerDishConsumptionChart
                key={chartConfig.key}
                data={displayedPerDishMetrics}
                consumptionType={chartConfig.consumptionType}
                title={chartConfig.title}
                loading={perDishDataInitialLoading}
                isRefetching={perDishDataIsRefetching}
                error={consumptionErrorMessage || undefined}
                goalPlan={
                  'goalChartKey' in chartConfig
                    ? (goalPlans?.[chartConfig.goalChartKey] ?? null)
                    : null
                }
              />
            ))}
            <GoalTrendChart
              title="Desvío de Residuos"
              unit="%"
              data={wasteDiversionData}
              goalPlan={goalPlans?.waste_diversion_pct ?? null}
            />
          </ChartGrid>
        </ChartSection>

        <ChartSection>
          <SectionHeader>
            <SectionTitle>Huella de carbono</SectionTitle>
            <SectionDescription>
              Revisa la evolución de CO₂e por plato y la contribución absoluta
              de cada alcance.
            </SectionDescription>
          </SectionHeader>
          <ChartGrid minRowHeight={400}>
            <PerDishConsumptionChart
              key={co2eChartConfig.key}
              data={displayedPerDishMetrics}
              consumptionType={co2eChartConfig.consumptionType}
              title={co2eChartConfig.title}
              loading={perDishDataInitialLoading}
              isRefetching={perDishDataIsRefetching}
              error={
                calendarizedError?.message ||
                coversError?.message ||
                calendarizedCO2eError?.message ||
                undefined
              }
              goalPlan={goalPlans?.kg_co2e ?? null}
            />
            <EmissionsByScopeChart
              data={emissionsByScopeData}
              coversData={coversVolumes?.items}
              loading={isLoadingEmissionsByScope}
              error={emissionsByScopeError?.message || undefined}
            />
          </ChartGrid>
        </ChartSection>
      </AnalyticsContent>
    </AnalyticsContainer>
  );
};
